import { lazy, Suspense } from 'react'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'

const COMPONENT_MAP: Record<string, React.LazyExoticComponent<React.ComponentType>> = {
  TaskList: lazy(() => import('@/features/tasks/components/TaskList').then(m => ({ default: m.TaskList }))),
  HabitTracker: lazy(() => import('@/features/tiles/components/HabitTracker').then(m => ({ default: m.HabitTracker }))),
  QuickStats: lazy(() => import('@/features/tiles/components/QuickStats').then(m => ({ default: m.QuickStats }))),
  WaterIntake: lazy(() => import('@/features/tiles/components/WaterIntake').then(m => ({ default: m.WaterIntake }))),
  Notes: lazy(() => import('@/features/tiles/components/Notes').then(m => ({ default: m.Notes }))),
  DeepWork: lazy(() => import('@/features/tiles/components/DeepWork').then(m => ({ default: m.DeepWork }))),
  Biometrics: lazy(() => import('@/features/tiles/components/Biometrics').then(m => ({ default: m.Biometrics }))),
  QuickActions: lazy(() => import('@/features/tiles/components/QuickActions').then(m => ({ default: m.QuickActions }))),
  DailyStoic: lazy(() => import('@/features/tiles/components/DailyStoic').then(m => ({ default: m.DailyStoic }))),
  Agenda: lazy(() => import('@/features/tiles/components/Agenda').then(m => ({ default: m.Agenda }))),
  BookTracker: lazy(() => import('@/features/tiles/components/BookTracker').then(m => ({ default: m.BookTracker }))),
  WorkflowQueue: lazy(() => import('@/features/tiles/components/WorkflowQueue').then(m => ({ default: m.WorkflowQueue }))),
  DailyBriefing: lazy(() => import('@/features/tiles/components/DailyBriefing').then(m => ({ default: m.DailyBriefing }))),
  Email: lazy(() => import('@/features/tiles/components/Email').then(m => ({ default: m.Email }))),
}

interface TileRendererProps {
  component: string
}

/** "WaterIntake" -> "Water Intake": the map key is a component identifier, and
 *  it ends up in a user-facing card header when a tile crashes. */
function humanizeTileName(component: string): string {
  return component.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
}

export function TileRenderer({ component }: TileRendererProps) {
  const Component = COMPONENT_MAP[component]

  if (!Component) {
    return null
  }

  return (
    <ErrorBoundary label={humanizeTileName(component)}>
      <Suspense fallback={<div className="animate-pulse bg-white/5 rounded-lg h-full min-h-[200px]" />}>
        <Component />
      </Suspense>
    </ErrorBoundary>
  )
}
